// Model Bench — compare person detectors on a frozen, uploaded image set.
//
// The Threshold Bench next door answers "where should the bar sit for the model we run?".
// This answers the question underneath it: "is this the model we should be running?" — and
// that one cannot be settled on production frames, because a comparison over frames that
// differ per model cannot separate "this model is better" from "these frames were easier".
// So the set is uploaded once, frozen, and every candidate sees exactly the same pixels.
//
// THE THING THIS PAGE EXISTS TO STOP YOU DOING: ranking models on recall. A model that fires
// on everything has perfect recall, so a set of person-only frames always crowns the most
// trigger-happy candidate. What prices recall is the person-FREE frames — how often a model
// blurs one, and how much of the shelf it destroys doing it. Every row here shows both, and
// when a dataset has no empty frames the page says the comparison cannot rank anything
// rather than quietly printing a winner.
//
// WHY THE SLIDER IS INSTANT: each image is scored ONCE per model, at a floor far below any
// usable threshold, so a single pass returns every candidate box. Recall, false-positive
// rate and destroyed area at every threshold are arithmetic over what already arrived. That
// is not just speed — scoring per position would mean sampling three or four thresholds by
// hand, and the behaviour that separates these models lives between the points anyone would
// pick (human-v4 beats human-v3 at 0.90 and loses to it at 0.85).
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card, Row, Col, Select, Button, Slider, Tag, Alert, Spin, Empty, Typography, Table,
  Upload, Modal, Progress, Space, Input, InputNumber, Radio, message, Tooltip, Statistic,
  Segmented, Popconfirm,
} from "antd";
import {
  ExperimentOutlined, ReloadOutlined, UploadOutlined, DeleteOutlined,
  ThunderboltOutlined, CheckCircleOutlined, PlusOutlined,
} from "@ant-design/icons";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  getHumanModels, getEvalDatasets, createEvalDataset, uploadEvalImages, getEvalImages,
  labelEvalImage, deleteEvalImage, fetchEvalImageBlob, startEvalRun, getEvalRun,
  cancelEvalRun, getEvalResults, promoteHumanModel,
} from "../network/api";

const { Title, Text, Paragraph } = Typography;

const GOOD = "#52c41a";
const WARN = "#faad14";
const CRIT = "#ff4d4f";
const MUTED = "#8c9bab";

// Distinct enough to stay separable when five models are on one chart, and stable per model
// index so a colour means the same series across the table, the chart and the thumbnails.
const SERIES = ["#1677ff", "#52c41a", "#fa8c16", "#eb2f96", "#13c2c2", "#722ed1", "#a0d911"];

const pct = (x) => (x == null || Number.isNaN(x) ? "—" : `${(x * 100).toFixed(1)}%`);
const num = (x, d = 3) => (x == null || Number.isNaN(x) ? "—" : Number(x).toFixed(d));

/**
 * Exact union area of axis-aligned boxes, as a fraction of the frame.
 *
 * Union, not the sum of box areas: overlapping detections would otherwise report more
 * destruction than the redactor actually does, and this number is the one that prices a
 * false positive. Coordinate compression keeps it exact — an approximation here would be
 * indistinguishable from a real difference between two models.
 */
function unionFraction(boxes, w, h) {
  if (!boxes.length || !w || !h) return 0;
  const xs = [...new Set(boxes.flatMap((b) => [b.x1, b.x2]))].sort((a, b) => a - b);
  const ys = [...new Set(boxes.flatMap((b) => [b.y1, b.y2]))].sort((a, b) => a - b);
  let area = 0;
  for (let i = 0; i < xs.length - 1; i += 1) {
    for (let j = 0; j < ys.length - 1; j += 1) {
      const cx = (xs[i] + xs[i + 1]) / 2;
      const cy = (ys[j] + ys[j + 1]) / 2;
      if (boxes.some((b) => b.x1 <= cx && cx <= b.x2 && b.y1 <= cy && cy <= b.y2)) {
        area += (xs[i + 1] - xs[i]) * (ys[j + 1] - ys[j]);
      }
    }
  }
  return Math.min(1, area / (w * h));
}

/**
 * Everything one model does to this dataset at one threshold.
 *
 * `recall` and `fpRate` are computed over DIFFERENT denominators on purpose — person frames
 * and empty frames — and an unlabelled image contributes to neither. Folding unlabelled
 * frames into "empty" would manufacture false positives out of frames nobody has looked at,
 * which is the fastest way to reject a good model.
 */
function metricsFor(rows, imagesById, t) {
  let personTotal = 0;
  let personHit = 0;
  let emptyTotal = 0;
  let emptyHit = 0;
  let failed = 0;
  let slivers = 0;
  const emptyBlur = [];
  const latency = [];

  for (const r of rows) {
    const img = imagesById.get(r.imageId);
    if (!img) continue;
    if (r.error) {
      failed += 1;
      continue;
    }
    if (r.ms != null) latency.push(r.ms);
    slivers += r.droppedDegenerate || 0;
    const hit = (r.maxScore ?? 0) >= t;
    if (img.label === "person") {
      personTotal += 1;
      if (hit) personHit += 1;
    } else if (img.label === "empty") {
      emptyTotal += 1;
      if (hit) emptyHit += 1;
      emptyBlur.push(unionFraction((r.boxes || []).filter((b) => b.score >= t), img.width, img.height));
    }
  }

  const mean = (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : null);
  const median = (a) => {
    if (!a.length) return null;
    const s = [...a].sort((x, y) => x - y);
    return s[Math.floor(s.length / 2)];
  };

  return {
    personTotal,
    personHit,
    recall: personTotal ? personHit / personTotal : null,
    emptyTotal,
    emptyHit,
    fpRate: emptyTotal ? emptyHit / emptyTotal : null,
    meanBlur: mean(emptyBlur),
    worstBlur: emptyBlur.length ? Math.max(...emptyBlur) : null,
    medianMs: median(latency),
    failed,
    slivers,
  };
}

/**
 * The lowest bar that still reaches a target recall, and what it costs there.
 *
 * This is the shape that actually decides a deploy, and it is why the table exists next to
 * the chart: recall on its own can always be bought by dropping the threshold until
 * everything fires, so the honest unit is the PAIR (threshold that hits the target, price
 * paid in false positives and destroyed shelf). Searching downward from the top returns the
 * highest threshold that works, i.e. the cheapest way to buy that recall.
 */
function costToReach(rows, imagesById, target, floor) {
  for (let t = 0.99; t >= floor; t -= 0.01) {
    const m = metricsFor(rows, imagesById, t);
    if (m.recall != null && m.recall >= target) {
      return { conf: t, fpRate: m.fpRate, blur: m.meanBlur };
    }
  }
  return null;
}

// Object URLs, cached per image for the life of the page. The grid re-renders constantly
// (every slider drag), and re-fetching a frame per render would put hundreds of authed
// requests on the server for pixels the browser already has.
const blobCache = new Map();

function EvalImage({ imageId, height = 96, boxes, imgW, imgH, onClick }) {
  const [url, setUrl] = useState(() => blobCache.get(imageId) || null);
  useEffect(() => {
    let dead = false;
    if (blobCache.has(imageId)) {
      setUrl(blobCache.get(imageId));
      return undefined;
    }
    fetchEvalImageBlob(imageId)
      .then((u) => {
        if (dead) return URL.revokeObjectURL(u);
        blobCache.set(imageId, u);
        setUrl(u);
        return undefined;
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, [imageId]);

  if (!url) {
    return (
      <div style={{ height, display: "grid", placeItems: "center", background: "#f0f2f5" }}>
        <Spin size="small" />
      </div>
    );
  }
  return (
    <div
      style={{ position: "relative", display: "inline-block", cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <img src={url} alt="" style={{ height, display: "block", borderRadius: 4 }} />
      {boxes && imgW && imgH ? (
        <svg
          viewBox={`0 0 ${imgW} ${imgH}`}
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        >
          {boxes.map((b, i) => (
            <rect
              key={i}
              x={b.x1}
              y={b.y1}
              width={b.x2 - b.x1}
              height={b.y2 - b.y1}
              fill="none"
              stroke={b.color || CRIT}
              strokeWidth={Math.max(2, imgW / 250)}
            />
          ))}
        </svg>
      ) : null}
    </div>
  );
}

const ModelBench = () => {
  const [roster, setRoster] = useState(null);
  const [rosterError, setRosterError] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [datasetId, setDatasetId] = useState(null);
  const [selectedModels, setSelectedModels] = useState([]);
  const [data, setData] = useState(null); // { images, results, stats, scanFloor }
  const [loading, setLoading] = useState(false);
  const [threshold, setThreshold] = useState(0.9);
  const [run, setRun] = useState(null);
  const [view, setView] = useState("Compare");
  const [detail, setDetail] = useState(null);
  const [newSet, setNewSet] = useState({ open: false, name: "", notes: "" });
  const [uploadLabel, setUploadLabel] = useState("person");
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef(null);

  const loadRoster = useCallback(async () => {
    try {
      const r = await getHumanModels();
      setRoster(r);
      setRosterError(null);
      if (r.humanConf != null) setThreshold(r.humanConf);
    } catch (err) {
      setRosterError(err?.response?.data?.error || err.message);
    }
  }, []);

  const loadDatasets = useCallback(async () => {
    const r = await getEvalDatasets();
    setDatasets(r.datasets || []);
    setDatasetId((cur) => cur || (r.datasets?.[0] ? r.datasets[0].id : null));
  }, []);

  const loadResults = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      setData(await getEvalResults(id));
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoster();
    loadDatasets().catch((err) => message.error(err.message));
  }, [loadRoster, loadDatasets]);

  useEffect(() => {
    loadResults(datasetId);
  }, [datasetId, loadResults]);

  // Poll a running job. Cleared on unmount so navigating away does not leave a timer
  // hitting the API for a run nobody is watching.
  useEffect(() => {
    if (!run || run.status !== "running") return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const r = await getEvalRun(run.id);
        setRun({ ...r.run, id: r.run.id || run.id });
        if (r.run.status !== "running") {
          clearInterval(pollRef.current);
          loadResults(datasetId);
        }
      } catch {
        clearInterval(pollRef.current);
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [run, datasetId, loadResults]);

  const imagesById = useMemo(
    () => new Map((data?.images || []).map((i) => [i.id, i])),
    [data]
  );
  const byModel = useMemo(() => {
    const m = new Map();
    for (const r of data?.results || []) {
      if (!m.has(r.modelId)) m.set(r.modelId, []);
      m.get(r.modelId).push(r);
    }
    return m;
  }, [data]);

  // Default the comparison to whatever has actually been scored, so the page opens on
  // something rather than on an empty table the user has to configure first.
  useEffect(() => {
    if (!data?.scoredModels?.length) return;
    setSelectedModels((cur) => (cur.length ? cur : data.scoredModels));
  }, [data]);

  const shown = useMemo(
    () => selectedModels.filter((m) => byModel.has(m)),
    [selectedModels, byModel]
  );

  const stats = data?.stats;
  const floor = data?.scanFloor ?? 0.05;
  // A dataset with no person-free frames cannot rank anything: recall is maximised by a
  // model that fires on every frame, so the trigger-happy candidate wins by construction.
  const canRank = Boolean(stats && stats.empty > 0 && stats.person > 0);

  const rows = useMemo(
    () =>
      shown.map((id, i) => ({
        key: id,
        modelId: id,
        colour: SERIES[i % SERIES.length],
        ...metricsFor(byModel.get(id) || [], imagesById, threshold),
      })),
    [shown, byModel, imagesById, threshold]
  );

  const sweep = useMemo(() => {
    if (!shown.length) return [];
    const out = [];
    for (let t = Math.round(floor * 100); t <= 99; t += 2) {
      const point = { t: t / 100 };
      for (const id of shown) {
        const m = metricsFor(byModel.get(id) || [], imagesById, t / 100);
        point[`${id}__recall`] = m.recall == null ? null : m.recall * 100;
        point[`${id}__fp`] = m.fpRate == null ? null : m.fpRate * 100;
      }
      out.push(point);
    }
    return out;
  }, [shown, byModel, imagesById, floor]);

  const targets = useMemo(() => {
    if (!canRank) return [];
    return [0.9, 0.95, 0.98, 0.99].map((target) => ({
      key: target,
      target,
      per: Object.fromEntries(
        shown.map((id) => [id, costToReach(byModel.get(id) || [], imagesById, target, floor)])
      ),
    }));
  }, [shown, byModel, imagesById, floor, canRank]);

  // Frames where the models do not agree at the current bar. This is where a human eye is
  // worth the most, and it costs nothing extra — the readings are already here.
  const disagreements = useMemo(() => {
    if (shown.length < 2) return [];
    const out = [];
    for (const img of data?.images || []) {
      const scores = shown.map((id) => {
        const r = (byModel.get(id) || []).find((x) => x.imageId === img.id);
        return { id, score: r && !r.error ? r.maxScore ?? 0 : null, boxes: r?.boxes || [] };
      });
      const known = scores.filter((s) => s.score != null);
      if (known.length < 2) continue;
      const verdicts = new Set(known.map((s) => s.score >= threshold));
      if (verdicts.size < 2) continue;
      const spread = Math.max(...known.map((s) => s.score)) - Math.min(...known.map((s) => s.score));
      out.push({ img, scores, spread });
    }
    return out.sort((a, b) => b.spread - a.spread);
  }, [data, shown, byModel, threshold]);

  const doRun = async (force = false) => {
    if (!datasetId || !selectedModels.length) return;
    try {
      const r = await startEvalRun({ datasetId, modelIds: selectedModels, force });
      setRun(r.run);
      if (r.run.status !== "running") {
        message.info(
          r.run.cached
            ? `Nothing to score — all ${r.run.cached} pairs were already measured.`
            : "Nothing to score."
        );
        loadResults(datasetId);
      }
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
    }
  };

  const doUpload = async ({ fileList }) => {
    const files = fileList.map((f) => f.originFileObj).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    try {
      const r = await uploadEvalImages(datasetId, files, uploadLabel);
      message.success(
        `${r.added} added` +
          (r.duplicates ? `, ${r.duplicates} already in the set` : "") +
          (r.rejected ? `, ${r.rejected} rejected` : "")
      );
      await loadDatasets();
      await loadResults(datasetId);
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
    } finally {
      setUploading(false);
    }
  };

  // NO Modal.confirm here, deliberately. antd's static Modal/notification helpers do not
  // render under React 19 — they fail silently, so the button appears to do nothing at all.
  // The rest of this codebase already worked around it (see ticketDetail.js), and every
  // confirmation elsewhere is a <Popconfirm>, which is a real component. This follows that.
  const doPromote = async (modelId) => {
    try {
      await promoteHumanModel(modelId);
      message.success(
        modelId
          ? `Ingest is now screening with ${modelId}`
          : "Pin cleared — the engine's own default applies again"
      );
      loadRoster();
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
    }
  };

  const PROMOTE_WARNING =
    "This changes what the whole fleet is redacted with, starting on the very next uploaded " +
    "frame. Nothing already stored is re-screened. Reversible from this page.";

  const columns = [
    {
      title: "Model",
      dataIndex: "modelId",
      render: (id, r) => (
        <Space>
          <span style={{ width: 8, height: 8, borderRadius: 8, background: r.colour, display: "inline-block" }} />
          <Text strong>{id}</Text>
          {/* Membership, not equality — with an ensemble pinned `effective` is
              "a:0.42,b:0.30" and a strict compare would show nothing as live. */}
          {specMembers(roster?.effective).includes(id) ? <Tag color="blue">live</Tag> : null}
        </Space>
      ),
    },
    {
      title: <Tooltip title="Person frames the model catches at this threshold. Half a number on its own — read it with the false-positive rate.">Recall</Tooltip>,
      dataIndex: "recall",
      sorter: (a, b) => (a.recall ?? -1) - (b.recall ?? -1),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: v == null ? MUTED : GOOD }}>{pct(v)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.personHit}/{r.personTotal}</Text>
        </Space>
      ),
    },
    {
      title: <Tooltip title="Person-FREE frames the model blurs. This is what recall costs.">False positives</Tooltip>,
      dataIndex: "fpRate",
      sorter: (a, b) => (a.fpRate ?? 2) - (b.fpRate ?? 2),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: v == null ? MUTED : v > 0.2 ? CRIT : v > 0.05 ? WARN : GOOD }}>
            {pct(v)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.emptyHit}/{r.emptyTotal}</Text>
        </Space>
      ),
    },
    {
      title: <Tooltip title="Mean fraction of a person-free frame destroyed, counting overlap once. This is irreversible shelf loss.">Shelf blurred</Tooltip>,
      dataIndex: "meanBlur",
      sorter: (a, b) => (a.meanBlur ?? 2) - (b.meanBlur ?? 2),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text>{pct(v)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>worst {pct(r.worstBlur)}</Text>
        </Space>
      ),
    },
    {
      title: <Tooltip title={`Boxes above the ${floor} scan floor too thin to hold a person. Production counts these at the redaction bar, not at the floor — a non-zero number here is not what production reports.`}>Slivers @ floor</Tooltip>,
      dataIndex: "slivers",
      render: (v) => (v ? <Tag color="orange">{v}</Tag> : <Text type="secondary">0</Text>),
    },
    {
      title: "Median ms",
      dataIndex: "medianMs",
      render: (v) => (v == null ? "—" : Math.round(v)),
    },
    {
      title: "Failed",
      dataIndex: "failed",
      render: (v) => (v ? <Tag color="red">{v}</Tag> : <Text type="secondary">0</Text>),
    },
    {
      title: "",
      key: "promote",
      render: (_, r) =>
        roster?.effective === r.modelId ? null : (
          <Popconfirm
            title={`Screen every upload with ${r.modelId}?`}
            description={<div style={{ maxWidth: 320 }}>{PROMOTE_WARNING}</div>}
            okText="Promote"
            cancelText="Cancel"
            onConfirm={() => doPromote(r.modelId)}
          >
            <Button size="small" icon={<ThunderboltOutlined />}>
              Promote
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>
        <ExperimentOutlined /> Model Bench — human detection
      </Title>
      <Paragraph type="secondary" style={{ maxWidth: 900 }}>
        Score a fixed set of images through several person detectors and compare them on the
        same pixels. Recall alone cannot rank these models — a detector that fires on
        everything has perfect recall — so every row shows what that recall costs on
        person-free frames.
      </Paragraph>

      {rosterError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="The detection engine is unreachable"
          description={`${rosterError} — models cannot be listed or scored until it answers.`}
        />
      ) : null}

      <LiveBanner roster={roster} onClear={() => doPromote(null)} onRefresh={loadRoster} />

      <EnsemblePicker roster={roster} onPromote={doPromote} />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={7}>
            <Text type="secondary" style={{ fontSize: 12 }}>Image set</Text>
            <Select
              style={{ width: "100%" }}
              value={datasetId}
              onChange={setDatasetId}
              placeholder="No image set yet"
              options={datasets.map((d) => ({
                value: d.id,
                label: `${d.name} — ${d.stats.total} images`,
              }))}
            />
          </Col>
          <Col xs={24} md={9}>
            <Text type="secondary" style={{ fontSize: 12 }}>Models to compare</Text>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              value={selectedModels}
              onChange={setSelectedModels}
              placeholder="Pick the detectors to score"
              options={(roster?.models || []).map((m) => ({
                value: m.id,
                label: m.id + (data?.scoredModels?.includes(m.id) ? " ✓" : ""),
              }))}
            />
          </Col>
          <Col xs={24} md={8}>
            <Space wrap style={{ marginTop: 18 }}>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setNewSet({ open: true, name: "", notes: "" })}
              >
                New image set
              </Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                disabled={!datasetId || !selectedModels.length || run?.status === "running"}
                onClick={() => doRun(false)}
              >
                Run
              </Button>
              <Tooltip title="Re-score every pair even if a reading already exists. Use after the engine image changes.">
                <Button
                  icon={<ReloadOutlined />}
                  disabled={!datasetId || !selectedModels.length || run?.status === "running"}
                  onClick={() => doRun(true)}
                >
                  Re-score
                </Button>
              </Tooltip>
              <Tooltip title={datasetId ? "" : "Create an image set first — frames are uploaded into one"}>
                <Upload
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={doUpload}
                  disabled={!datasetId || uploading}
                >
                  <Button icon={<UploadOutlined />} loading={uploading} disabled={!datasetId}>
                    Upload images
                  </Button>
                </Upload>
              </Tooltip>
              <Radio.Group
                size="small"
                value={uploadLabel}
                onChange={(e) => setUploadLabel(e.target.value)}
                options={[
                  { label: "as person", value: "person" },
                  { label: "as empty", value: "empty" },
                  { label: "unlabelled", value: "unlabelled" },
                ]}
                optionType="button"
              />
            </Space>
          </Col>
        </Row>

        {stats ? <DatasetStats stats={stats} canRank={canRank} /> : null}
        {run && run.status === "running" ? (
          <RunProgress
            run={run}
            onCancel={async () => {
              await cancelEvalRun(run.id).catch(() => {});
            }}
          />
        ) : null}
        {run && run.status === "failed" ? (
          <Alert type="error" showIcon style={{ marginTop: 12 }} message={run.error || "the run failed"} />
        ) : null}
      </Card>

      {!datasetId ? (
        <Card>
          <Empty description="No image set yet. Create one, upload frames, then run the models against it.">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setNewSet({ open: true, name: "", notes: "" })}
            >
              New image set
            </Button>
          </Empty>
        </Card>
      ) : (
        <Spin spinning={loading}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col xs={24} md={16}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Redaction threshold — everything below is re-evaluated in the browser, no
                  re-scoring
                </Text>
                <Slider
                  min={floor}
                  max={0.99}
                  step={0.01}
                  value={threshold}
                  onChange={setThreshold}
                  marks={{
                    [floor]: String(floor),
                    0.5: "0.50",
                    0.85: "0.85",
                    0.9: "0.90",
                    0.99: "0.99",
                  }}
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic title="Threshold" value={threshold} precision={2} />
                {roster?.humanConf != null && Math.abs(roster.humanConf - threshold) > 0.001 ? (
                  <Text type="warning" style={{ fontSize: 12 }}>
                    production redacts at {num(roster.humanConf, 2)}
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    this is what production redacts at
                  </Text>
                )}
              </Col>
            </Row>
          </Card>

          <Segmented
            style={{ marginBottom: 16 }}
            value={view}
            onChange={setView}
            options={["Compare", `Disagreements (${disagreements.length})`, "Images"]}
          />

          {view === "Compare" ? (
            <>
              <Card size="small" title={`At threshold ${num(threshold, 2)}`} style={{ marginBottom: 16 }}>
                {!canRank ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="This set cannot rank models yet"
                    description={
                      stats?.empty === 0
                        ? "There are no person-free frames in it. Recall is trivially maximised by a model that fires on everything, so without empty frames the most trigger-happy candidate always wins. Add frames you have confirmed contain nobody, and label them 'empty'."
                        : "There are no frames labelled 'person', so recall cannot be measured."
                    }
                  />
                ) : null}
                <Table
                  size="small"
                  columns={columns}
                  dataSource={rows}
                  pagination={false}
                  locale={{ emptyText: "Pick models and press Run." }}
                />
              </Card>

              {shown.length ? (
                <Row gutter={16}>
                  <Col xs={24} lg={14}>
                    <Card size="small" title="Recall against its cost, across the whole range">
                      <Paragraph type="secondary" style={{ fontSize: 12 }}>
                        Solid = person frames caught. Dashed = person-free frames blurred. A
                        model is better where its solid line is higher AND its dashed line is
                        lower; where both rise together it has only become more trigger-happy.
                      </Paragraph>
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={sweep}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="t" tickFormatter={(v) => v.toFixed(2)} />
                          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                          <RTooltip
                            formatter={(v, n) => [v == null ? "—" : `${v.toFixed(1)}%`, n]}
                            labelFormatter={(v) => `conf ${Number(v).toFixed(2)}`}
                          />
                          <Legend />
                          {shown.map((id, i) => (
                            <Line
                              key={`${id}-r`}
                              type="monotone"
                              dataKey={`${id}__recall`}
                              name={`${id} recall`}
                              stroke={SERIES[i % SERIES.length]}
                              dot={false}
                              strokeWidth={2}
                            />
                          ))}
                          {shown.map((id, i) => (
                            <Line
                              key={`${id}-f`}
                              type="monotone"
                              dataKey={`${id}__fp`}
                              name={`${id} false pos.`}
                              stroke={SERIES[i % SERIES.length]}
                              strokeDasharray="4 3"
                              dot={false}
                              strokeWidth={1.5}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card size="small" title="What each recall target costs">
                      <Paragraph type="secondary" style={{ fontSize: 12 }}>
                        The highest threshold that still reaches the target, and the price
                        paid there. "unreachable" means no threshold gets that model to this
                        recall on this set at all.
                      </Paragraph>
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={targets}
                        columns={[
                          {
                            title: "Recall",
                            dataIndex: "target",
                            render: (v) => <Text strong>{pct(v)}</Text>,
                          },
                          ...shown.map((id) => ({
                            title: id,
                            key: id,
                            render: (_, row) => {
                              const c = row.per[id];
                              if (!c) return <Text type="secondary">unreachable</Text>;
                              return (
                                <Space direction="vertical" size={0}>
                                  <Text>conf {num(c.conf, 3)}</Text>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {pct(c.fpRate)} FP · {pct(c.blur)} blurred
                                  </Text>
                                </Space>
                              );
                            },
                          })),
                        ]}
                        locale={{ emptyText: "Needs both person and person-free frames." }}
                      />
                    </Card>
                  </Col>
                </Row>
              ) : null}
            </>
          ) : null}

          {view.startsWith("Disagreements") ? (
            <Card size="small" title={`Frames the models judge differently at ${num(threshold, 2)}`}>
              <Paragraph type="secondary" style={{ fontSize: 12 }}>
                Sorted by how far apart the scores are. These are the frames worth looking at
                with your own eyes — every other frame the models already agree on.
              </Paragraph>
              {shown.length < 2 ? (
                <Empty description="Pick at least two models." />
              ) : !disagreements.length ? (
                <Empty description="No disagreements at this threshold." />
              ) : (
                <Row gutter={[12, 12]}>
                  {disagreements.slice(0, 60).map(({ img, scores, spread }) => (
                    <Col key={img.id} xs={12} sm={8} md={6} lg={4}>
                      <Card
                        size="small"
                        styles={{ body: { padding: 8 } }}
                        cover={
                          <EvalImage
                            imageId={img.id}
                            height={110}
                            onClick={() => setDetail({ img, scores })}
                          />
                        }
                      >
                        <Space direction="vertical" size={2} style={{ width: "100%" }}>
                          <LabelTag label={img.label} />
                          {scores.map((s) => (
                            <Text key={s.id} style={{ fontSize: 11 }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 6,
                                  height: 6,
                                  borderRadius: 6,
                                  marginRight: 4,
                                  background: SERIES[shown.indexOf(s.id) % SERIES.length],
                                }}
                              />
                              {s.score == null ? "—" : num(s.score)}
                              {s.score != null && s.score >= threshold ? (
                                <Tag color="red" style={{ marginLeft: 4, fontSize: 10, lineHeight: "14px" }}>
                                  blurs
                                </Tag>
                              ) : null}
                            </Text>
                          ))}
                          <Text type="secondary" style={{ fontSize: 10 }}>Δ {num(spread)}</Text>
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
              {disagreements.length > 60 ? (
                <Text type="secondary">
                  Showing the 60 widest of {disagreements.length}. Raise the threshold or
                  narrow the model selection to see the rest.
                </Text>
              ) : null}
            </Card>
          ) : null}

          {view === "Images" ? (
            <ImageGrid
              datasetId={datasetId}
              onChanged={() => {
                loadDatasets();
                loadResults(datasetId);
              }}
            />
          ) : null}
        </Spin>
      )}

      <Modal
        open={Boolean(detail)}
        onCancel={() => setDetail(null)}
        footer={null}
        width={900}
        title={detail ? detail.img.filename : ""}
      >
        {detail ? (
          <>
            <EvalImage
              imageId={detail.img.id}
              height={520}
              imgW={detail.img.width}
              imgH={detail.img.height}
              boxes={detail.scores.flatMap((s) =>
                (s.boxes || [])
                  .filter((b) => b.score >= threshold)
                  .map((b) => ({ ...b, color: SERIES[shown.indexOf(s.id) % SERIES.length] }))
              )}
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              Boxes are what each model would destroy at {num(threshold, 2)}, already dilated
              and clamped exactly as the redactor applies them.
            </Paragraph>
          </>
        ) : null}
      </Modal>

      <Modal
        open={newSet.open}
        title="New image set"
        okText="Create"
        onCancel={() => setNewSet({ open: false, name: "", notes: "" })}
        onOk={async () => {
          try {
            const r = await createEvalDataset({ name: newSet.name, notes: newSet.notes });
            setNewSet({ open: false, name: "", notes: "" });
            await loadDatasets();
            setDatasetId(r.dataset.id);
          } catch (err) {
            message.error(err?.response?.data?.error || err.message);
          }
        }}
      >
        <Input
          placeholder="Name, e.g. chiller-eval-2026-08"
          value={newSet.name}
          onChange={(e) => setNewSet((s) => ({ ...s, name: e.target.value }))}
          style={{ marginBottom: 8 }}
        />
        <Input.TextArea
          rows={3}
          placeholder="Where these frames came from. An eval set whose provenance nobody remembers cannot be defended when its verdict is questioned — and it will be."
          value={newSet.notes}
          onChange={(e) => setNewSet((s) => ({ ...s, notes: e.target.value }))}
        />
      </Modal>
    </div>
  );
};

/**
 * Which model is live — three facts kept apart on purpose.
 *
 * `observed` is the only one that is evidence: it is read off the most recently screened
 * production frame. The other two are intentions, and this subsystem has twice recorded a
 * model as live in a wiki page and a compose comment while production went on serving the
 * previous one. Showing all three makes a disagreement visible instead of discoverable
 * months later.
 */
function LiveBanner({ roster, onClear, onRefresh }) {
  if (!roster) return null;
  const drift = roster.observed && roster.observed.model !== roster.effective;
  return (
    <Alert
      type={drift ? "warning" : "info"}
      showIcon
      style={{ marginBottom: 16 }}
      message={
        <Space wrap>
          <span>
            Ingest screens with <Tag color="blue">{roster.effective || "—"}</Tag>
            at conf <Text code>{num(roster.humanConf, 2)}</Text>
          </span>
          {roster.pinned ? (
            <Tag>pinned in DMS</Tag>
          ) : (
            <Tag>engine default ({roster.engineDefault})</Tag>
          )}
          {roster.redactEnabled === false ? <Tag color="orange">redaction OFF</Tag> : null}
        </Space>
      }
      description={
        <Space direction="vertical" size={2}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {roster.observed
              ? `Last frame production actually screened used ${roster.observed.model}` +
                (roster.observed.at ? ` at ${new Date(roster.observed.at).toLocaleString()}` : "")
              : "No screened production frame on record yet."}
          </Text>
          {drift ? (
            <Text type="warning" style={{ fontSize: 12 }}>
              That is not the model configured above. Until a frame arrives after the change,
              this is expected — if it persists, what is running is not what is set.
            </Text>
          ) : null}
          <Space size="small">
            <Button size="small" type="link" icon={<ReloadOutlined />} onClick={onRefresh}>
              Refresh
            </Button>
            {roster.pinned ? (
              <Popconfirm
                title="Hand the choice back to the engine?"
                description={
                  <div style={{ maxWidth: 320 }}>
                    Ingest will screen with the engine's own default on the next uploaded frame.
                  </div>
                }
                okText="Clear pin"
                cancelText="Cancel"
                onConfirm={onClear}
              >
                <Button size="small" type="link">
                  Clear the pin (back to the engine default)
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        </Space>
      }
    />
  );
}

/**
 * The ids inside a pin, which may be one model or an ensemble spec.
 * "a" -> ["a"];  "a:0.42,b:0.30" -> ["a", "b"].
 */
function specMembers(spec) {
  if (!spec) return [];
  return String(spec)
    .split(",")
    .map((p) => p.split(":")[0].trim())
    .filter(Boolean);
}

/**
 * Pin SEVERAL detectors at once. Ingest then runs each one and redacts the UNION.
 *
 * WHY THIS EXISTS (O101). These models do not fail in the same places and neither
 * dominates, so picking one is picking which people to leak: human-v6-ep60 scored a shopper
 * filling ~40% of a sharp frame at 0.07-0.27 and shipped his face, where human-v5-ep35
 * scores those same frames 0.66-0.90; v5 in turn misses the forearm reaching into a chiller
 * that v6 catches. The union leaks neither, and the trade is asymmetric — a false positive
 * blurs a frame compliance discards anyway, a false negative ships a recognisable face.
 *
 * The per-model threshold is not a nicety. A raw confidence is each model's OWN calibration
 * and these are not on a shared scale, so one global bar necessarily runs at least one of
 * them where nobody chose to. Leave a box empty and that model uses the global humanConf.
 */
function EnsemblePicker({ roster, onPromote }) {
  const [ids, setIds] = useState([]);
  const [confs, setConfs] = useState({});

  // Seed from what is actually live, so the control opens on current behaviour and any
  // change on screen is a change the operator made.
  useEffect(() => {
    if (!roster?.effective) return;
    const parts = String(roster.effective).split(",").map((p) => p.trim()).filter(Boolean);
    setIds(parts.map((p) => p.split(":")[0]));
    setConfs(
      Object.fromEntries(
        parts
          .filter((p) => p.includes(":"))
          .map((p) => [p.split(":")[0], Number(p.split(":")[1])])
      )
    );
  }, [roster?.effective]);

  const options = (roster?.models || []).map((m) => ({ value: m.id, label: m.id }));
  const spec = ids.map((id) => (confs[id] != null ? `${id}:${confs[id]}` : id)).join(",");
  const unchanged = spec === (roster?.effective || "");

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Space wrap align="center">
          <Text strong>Screen with several detectors</Text>
          <Tooltip title="Every selected model runs on every uploaded frame and the redactions are combined. Use it when the models miss different people.">
            <Tag>union</Tag>
          </Tooltip>
        </Space>

        <Space wrap align="start">
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 340 }}
            placeholder="Pick one or more person detectors"
            value={ids}
            options={options}
            onChange={setIds}
          />
          {ids.map((id) => (
            <Space key={id} size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {id} @
              </Text>
              <InputNumber
                size="small"
                min={0}
                max={1}
                step={0.01}
                style={{ width: 90 }}
                placeholder={num(roster?.humanConf, 2)}
                value={confs[id] ?? null}
                onChange={(v) => setConfs((c) => ({ ...c, [id]: v }))}
              />
            </Space>
          ))}
        </Space>

        <Text type="secondary" style={{ fontSize: 12 }}>
          Leave a threshold blank and that model uses the global bar
          (<Text code>{num(roster?.humanConf, 2)}</Text>). A raw confidence is each model's own
          calibration, so the same number does not mean the same thing to two detectors —
          human-v5-ep35 operates near 0.42 where human-v6-ep60 operates near 0.30.
        </Text>

        <Space wrap align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>
            Will pin:
          </Text>
          <Text code>{spec || "—"}</Text>
          {ids.length > 1 ? (
            <Tag color="orange">
              {ids.length} inferences per frame — ingest is synchronous, so screening takes
              roughly {ids.length}× as long
            </Tag>
          ) : null}
        </Space>

        <Popconfirm
          title={ids.length > 1 ? "Screen every upload with all of these?" : `Screen every upload with ${spec}?`}
          description={
            <div style={{ maxWidth: 360 }}>
              This changes what the whole fleet is redacted with, starting on the very next
              uploaded frame. Nothing already stored is re-screened. Reversible from this page.
            </div>
          }
          okText="Promote"
          cancelText="Cancel"
          onConfirm={() => onPromote(spec)}
          disabled={!ids.length || unchanged}
        >
          <Button
            type="primary"
            size="small"
            icon={<ThunderboltOutlined />}
            disabled={!ids.length || unchanged}
          >
            {unchanged && ids.length ? "Already live" : "Promote this combination"}
          </Button>
        </Popconfirm>
      </Space>
    </Card>
  );
}

function DatasetStats({ stats, canRank }) {
  return (
    <Space wrap style={{ marginTop: 12 }}>
      <Tag color="blue">{stats.total} images</Tag>
      <Tag color={stats.person ? "green" : "default"}>{stats.person} person</Tag>
      <Tag color={stats.empty ? "green" : "red"}>{stats.empty} person-free</Tag>
      {stats.unlabelled ? (
        <Tooltip title="Unlabelled frames contribute to no statistic — neither recall nor false positives. Label them in the Images tab.">
          <Tag color="orange">{stats.unlabelled} unlabelled</Tag>
        </Tooltip>
      ) : null}
      {canRank ? (
        <Tag icon={<CheckCircleOutlined />} color="success">
          can rank models
        </Tag>
      ) : null}
    </Space>
  );
}

function RunProgress({ run, onCancel }) {
  const pctDone = run.total ? Math.round((run.done / run.total) * 100) : 100;
  return (
    <div style={{ marginTop: 12 }}>
      <Progress percent={pctDone} status="active" />
      <Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {run.done}/{run.total} scored
          {run.cached ? `, ${run.cached} already measured` : ""}
          {run.failed ? `, ${run.failed} failed` : ""}
        </Text>
        <Button size="small" type="link" danger onClick={onCancel}>
          Cancel
        </Button>
      </Space>
    </div>
  );
}

function LabelTag({ label }) {
  if (label === "person") return <Tag color="green">person</Tag>;
  if (label === "empty") return <Tag color="blue">person-free</Tag>;
  return <Tag color="orange">unlabelled</Tag>;
}

/** The set itself — look at the frames, fix a wrong label, drop a frame that should not be here. */
function ImageGrid({ datasetId, onChanged }) {
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await getEvalImages(datasetId, filter || undefined);
      setImages(r.images || []);
    } finally {
      setBusy(false);
    }
  }, [datasetId, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setLabel = async (id, label) => {
    await labelEvalImage(id, label);
    await load();
    onChanged();
  };

  return (
    <Card
      size="small"
      title="The image set"
      extra={
        <Radio.Group
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          options={[
            { label: "all", value: "" },
            { label: "person", value: "person" },
            { label: "person-free", value: "empty" },
            { label: "unlabelled", value: "unlabelled" },
          ]}
          optionType="button"
        />
      }
    >
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        A label is ground truth: every number this page prints is downstream of it. Count a
        partial person — a hand reaching in, a face at the frame edge, a sleeve — as
        "person". Those are exactly the frames where these models diverge, and calling them
        person-free hides the difference being measured.
      </Paragraph>
      <Spin spinning={busy}>
        <Row gutter={[12, 12]}>
          {images.map((img) => (
            <Col key={img.id} xs={12} sm={8} md={6} lg={4}>
              <Card size="small" styles={{ body: { padding: 8 } }} cover={<EvalImage imageId={img.id} height={110} />}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text ellipsis style={{ fontSize: 11 }} title={img.filename}>
                    {img.filename}
                  </Text>
                  <Radio.Group
                    size="small"
                    value={img.label}
                    onChange={(e) => setLabel(img.id, e.target.value)}
                    options={[
                      { label: "person", value: "person" },
                      { label: "empty", value: "empty" },
                    ]}
                    optionType="button"
                  />
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={async () => {
                      await deleteEvalImage(img.id);
                      await load();
                      onChanged();
                    }}
                  >
                    Remove
                  </Button>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
        {!images.length && !busy ? <Empty description="No images here yet." /> : null}
      </Spin>
    </Card>
  );
}

export default ModelBench;
